<template>
	<div
		class="ui-file field-load"
		:class="{ 'field-load_warning': warning && !saveFile, 'field-load_dragging': isDragging }"
		@dragover.prevent="dragover"
		@dragleave="dragleave"
		@drop.prevent="drop"
	>
		<div
			class="ui-file__area-load"
			v-if="$slots.areaLoad"
			@dragover.prevent.stop="mouseEnterHandler"
		>
			<slot name="areaLoad" />
			<div
				class="area"
				@dragover.prevent.stop="dragover"
				@dragleave.stop="dragleave"
				@drop.prevent.stop="drop"
				v-show="isHover"
			></div>
			<div
				class="flare"
				v-if="isLoading"
			></div>
		</div>
		<div
			class="area-load-empty"
			v-else
		>
			<div :class="['field-load_empty', `field-load_empty_${orientation}`]">
				<slot name="title"></slot>
				<slot name="subtitle"></slot>
				<label
					class="btn__load"
					@click.prevent="showPickerMenu = true"
					:style="{ width: width }"
				>
					<div class="wrapper">
						<div class="click-area"></div>
						<div
							class="flare"
							v-if="isLoading"
						></div>
						<div v-if="$slots.customBtn">
							<slot name="customBtn" />
						</div>
						<UiButton
							v-else
							:label="label"
							:color="ButtonType.primary"
							:disabled="isDisabled"
							:size="ButtonSizeType.medium"
							:customStyle="{ width: width }"
						>
							<template #before>
								<UiIcon
									name="camera"
									folder="cargos"
									width="20px"
									height="20px"
									color="var(--white)"
								/>
							</template>
						</UiButton>
					</div>
				</label>
			</div>
			<div
				class="field-load_filled"
				v-if="isNeedFilledInfo"
			>
				<div class="content">
					<div
						class="title"
						v-if="fileName"
					>
						{{ fileName }}
					</div>
					<div
						class="subtitle"
						v-if="fileSize"
					>
						размер файла {{ fileSize }}
					</div>
				</div>
				<UiIcon
					name="cross"
					class="btn__delete"
					@click="deleteFileHandler"
				/>
			</div>
		</div>

		<input
			ref="photoInput"
			class="form-upload__input"
			type="file"
			name="photo"
			accept="image/*"
			:multiple="isMultiple"
			:disabled="isLoading || isDisabled"
			@change="handlePhotoInput"
		/>
		<input
			ref="fileInput"
			class="form-upload__input"
			type="file"
			name="files"
			:accept="fileAccept"
			:multiple="isMultiple"
			:disabled="isLoading || isDisabled"
			@change="handleFileInput"
		/>
		<input
			ref="cameraInput"
			class="form-upload__input"
			type="file"
			name="camera"
			:accept="props.acceptFiles"
			capture="environment"
			:disabled="isLoading || isDisabled"
			@change="handleCameraInput"
		/>

		<Teleport to="body">
			<Transition name="picker-menu">
				<div
					v-if="showPickerMenu"
					class="picker-menu-overlay"
					@click="showPickerMenu = false"
				>
					<div
						class="picker-menu-sheet"
						@click.stop
					>
						<div class="picker-menu-sheet__handle"></div>
						<div class="picker-menu-sheet__title">Выберите источник</div>
						<button
							class="picker-menu-sheet__item"
							@click="selectCamera"
						>
							<Icon
								name="material-symbols:add-a-photo-outline"
								color="var(--violet)"
								width="24px"
								height="24px"
							/>
							<span>Сделать фото</span>
						</button>
						<button
							class="picker-menu-sheet__item"
							@click="selectPhoto"
							v-if="isNative"
						>
							<Icon
								name="material-symbols:gallery-thumbnail-outline"
								folder="cargos"
								width="24px"
								height="24px"
								color="var(--violet)"
							/>
							<span>Галерея</span>
						</button>
						<button
							class="picker-menu-sheet__item"
							@click="selectFiles"
							v-else
						>
							<Icon
								name="ic:baseline-drive-folder-upload"
								folder="cargos"
								width="24px"
								height="24px"
								color="var(--violet)"
							/>
							<span>Все файлы</span>
						</button>
						<button
							class="picker-menu-sheet__cancel"
							@click="showPickerMenu = false"
						>
							Отмена
						</button>
					</div>
				</div>
			</Transition>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
	import { formatBytes, getFileExtension } from '~/helpers/file';
	import { ButtonSizeType, ButtonType } from '~~/shared/interfaces/ui';
	import { useNotificationStore } from '~/store/notification.store';
	import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
	import { Capacitor } from '@capacitor/core';
	import { FilePicker } from '@capawesome/capacitor-file-picker';

	const props = withDefaults(
		defineProps<{
			file?: any;
			warning?: boolean;
			isNeedFilledInfo?: boolean;
			isNeedPosibleDragAndDropFile?: boolean;
			orientation?: 'vertical' | 'horizontal';
			maxSize?: number | null;
			borderRadiusDragableArea?: string;
			label?: string;
			acceptFiles?: string;
			isLoading?: boolean;
			isMultiple?: boolean;
			isDisabled?: boolean;
			width?: string;
		}>(),
		{
			isNeedFilledInfo: false,
			isNeedPosibleDragAndDropFile: true,
			orientation: 'horizontal',
			maxSize: null,
			borderRadiusDragableArea: '20px',
			label: 'Загрузить',
			acceptFiles: '.pdf,.jpg,.svg,.jpeg,.png,.word,.txt,.webp,.excel',
			isMultiple: false,
			width: 'fit-content'
		}
	);

	const notificationStore = useNotificationStore();
	const emits = defineEmits(['load']);
	const { t } = useI18n();
	const isDragging = ref(false);
	const isHover = ref(false);
	const showPickerMenu = ref(false);

	const saveFile = ref<{ file: any; blobUrl: string } | null>(null);
	const photoInput = ref<HTMLInputElement | null>(null);
	const fileInput = ref<HTMLInputElement | null>(null);
	const cameraInput = ref<HTMLInputElement | null>(null);

	const isNative = computed(() => Capacitor.isNativePlatform());

	const fileAccept = computed(() => {
		return props.acceptFiles.replace(/image\/\*,?/g, '').trim();
	});

	const fileSize = computed(() => {
		if (saveFile.value) {
			return formatBytes(saveFile.value.file.size);
		}
	});
	const fileName = computed(() => {
		if (saveFile.value) {
			return saveFile.value.file.name;
		}
	});

	const revokePreviousBlob = () => {
		if (saveFile.value?.blobUrl) {
			URL.revokeObjectURL(saveFile.value.blobUrl);
		}
	};

	const selectCamera = async () => {
		showPickerMenu.value = false;

		if (isNative.value) {
			try {
				const photo = await Camera.getPhoto({
					quality: 90,
					allowEditing: false,
					resultType: CameraResultType.Uri,
					source: CameraSource.Camera,
					correctOrientation: true
				});

				await processCameraPhoto(photo);
			} catch (error: any) {
				if (error?.message !== 'User cancelled photos') {
					console.error('Camera error:', error);
				}
			}
		} else {
			cameraInput.value?.click();
		}
	};

	const selectPhoto = async () => {
		showPickerMenu.value = false;

		if (isNative.value) {
			try {
				const result = await FilePicker.pickImages({
					multiple: props.isMultiple
				});

				await processNativeFiles(result.files);
			} catch (error: any) {
				if (error?.message && !error.message.includes('cancel')) {
					console.error('Photo picker error:', error);
				}
			}
		} else {
			photoInput.value?.click();
		}
	};

	const selectFiles = async () => {
		showPickerMenu.value = false;

		fileInput.value?.click();
	};

	const processCameraPhoto = async (photo: any) => {
		try {
			let blob: Blob;

			if (photo.path) {
				const url = Capacitor.convertFileSrc(photo.path);
				const response = await fetch(url);
				blob = await response.blob();
			} else if (photo.webPath) {
				const response = await fetch(photo.webPath);
				blob = await response.blob();
			} else if (photo.base64String) {
				const byteString = atob(photo.base64String);
				const byteArray = new Uint8Array(byteString.length);
				for (let i = 0; i < byteString.length; i++) {
					byteArray[i] = byteString.charCodeAt(i);
				}
				blob = new Blob([byteArray], { type: 'image/jpeg' });
			} else {
				throw new Error('No photo data');
			}

			const fileName = `photo_${Date.now()}.jpg`;
			const fileObj = new File([blob], fileName, { type: 'image/jpeg' });

			const result = {
				file: fileObj,
				blobUrl: URL.createObjectURL(blob)
			};

			if (props.isMultiple) {
				emits('load', [result]);
			} else {
				revokePreviousBlob();
				saveFile.value = result;
				submitSavedFile();
			}
		} catch (error) {
			console.error('Process photo error:', error);
			notificationStore.addNewAlert('Ошибка обработки фото', 4000);

			emits('load', props.isMultiple ? [] : null);
		}
	};

	const processNativeFiles = async (files: any[]) => {
		if (!files?.length) return;

		const validFiles: { file: File; blobUrl: string }[] = [];

		for (const f of files) {
			try {
				let blob: Blob;

				if (f.blob) {
					blob = f.blob;
				} else if (f.uri) {
					const response = await fetch(f.uri);
					blob = await response.blob();
				} else if (f.path) {
					const url = Capacitor.convertFileSrc(f.path);
					const response = await fetch(url);
					blob = await response.blob();
				} else {
					continue;
				}

				const fileName = f.name || `file_${Date.now()}.jpg`;
				const mimeType = f.mimeType || blob.type || 'image/jpeg';

				const fileObj = new File([blob], fileName, { type: mimeType });

				// if (checkFileAccept(fileObj)) continue;

				validFiles.push({
					file: fileObj,
					blobUrl: URL.createObjectURL(blob)
				});
			} catch (error) {
				console.error('Error processing native file:', error);
			}
		}

		if (!validFiles.length) return;

		if (props.isMultiple) {
			emits('load', validFiles);
		} else {
			revokePreviousBlob();
			saveFile.value = validFiles[0];
			submitSavedFile();
		}
	};

	const handlePhotoInput = (e: Event) => {
		const input = e.target as HTMLInputElement;
		const files = Array.from(input.files ?? []);
		handleInputFiles(files);
		input.value = '';
	};

	const handleFileInput = (e: Event) => {
		const input = e.target as HTMLInputElement;
		const files = Array.from(input.files ?? []);
		handleInputFiles(files);
		input.value = '';
	};

	const handleCameraInput = (e: Event) => {
		const input = e.target as HTMLInputElement;
		const files = Array.from(input.files ?? []);
		handleInputFiles(files);
		input.value = '';
	};

	const handleInputFiles = (files: File[]) => {
		if (!files.length) return;

		if (props.isMultiple) {
			const validFiles: File[] = [];
			for (const file of files) {
				if (!checkFileAccept(file)) {
					validFiles.push(file);
				}
			}

			if (!validFiles.length) return;

			const results = validFiles.map((f) => ({
				file: f,
				blobUrl: URL.createObjectURL(f)
			}));

			emits('load', results);
		} else {
			const firstFile = files[0];
			if (!firstFile || checkFileAccept(firstFile)) return;

			revokePreviousBlob();
			saveFile.value = {
				file: firstFile,
				blobUrl: URL.createObjectURL(firstFile)
			};
			submitSavedFile();
		}
	};

	const mouseLeaveHandler = () => {
		isHover.value = false;
	};

	const mouseEnterHandler = () => {
		isHover.value = true;
	};

	const deleteFileHandler = () => {
		revokePreviousBlob();
		saveFile.value = null;
		if (photoInput.value) photoInput.value.value = '';
		if (fileInput.value) fileInput.value.value = '';
		if (cameraInput.value) cameraInput.value.value = '';
		submitSavedFile();
	};

	const dragover = () => {
		if (!props.isNeedPosibleDragAndDropFile) return;
		isDragging.value = true;
	};

	const dragleave = () => {
		if (!props.isNeedPosibleDragAndDropFile) return;
		isDragging.value = false;
	};

	const drop = (e: DragEvent) => {
		mouseLeaveHandler();
		if (!props.isNeedPosibleDragAndDropFile) return;

		isDragging.value = false;
		const droppedFiles = Array.from(e.dataTransfer?.files ?? []);
		handleInputFiles(droppedFiles);
	};

	const submitSavedFile = () => {
		emits('load', saveFile.value);

		setTimeout(() => {
			saveFile.value = null;
		});
	};

	const checkFileAccept = (file?: File) => {
		if (!file) return false;

		if (props.acceptFiles === 'image/*') {
			return !file.type.startsWith('image/');
		}

		if (props.acceptFiles && !props.acceptFiles.includes(getFileExtension(file.name))) {
			notificationStore.addNewAlert(`Формат не поддерживается, разрешённые форматы: ${props.acceptFiles}`, 4000);
			return true;
		}

		if (props.maxSize && props.maxSize < file.size) {
			notificationStore.addNewAlert(`Размер файла превышает ${formatBytes(props.maxSize)}`, 3000);
			return true;
		}
		return false;
	};

	onMounted(() => {
		saveFile.value = props.file;
	});

	onUnmounted(() => {
		revokePreviousBlob();
	});

	watch(
		() => props.file,
		() => {
			saveFile.value = props.file;
		}
	);
</script>

<style scoped lang="scss">
	.ui-file {
		overflow: hidden;
		position: relative;

		&__area-load {
			overflow: hidden;

			.area {
				position: absolute;
				top: 0;
				z-index: 5;
				left: 0;
				right: 0;
				bottom: 0;
			}
		}

		input {
			display: none;
		}
	}

	.field-load {
		border: 2px dashed transparent;
		border-radius: v-bind(borderRadiusDragableArea);

		&_dragging {
			cursor: grab !important;
			border: 2px dashed var(--gray-2);
		}

		&_warning {
			border: 2px dashed var(--var-warning);
		}

		&_empty {
			display: flex;
			align-items: center;
			border-radius: v-bind(borderRadiusDragableArea);
			gap: $g-3;
			justify-content: space-between;

			&_vertical {
				flex-direction: column;
				align-items: flex-start;
			}

			.btn {
				&__load {
					border-radius: v-bind(borderRadiusDragableArea);
					color: var(--var-blue-50);
					border: 1px solid var(--var-blue-50);
					cursor: pointer;

					.wrapper {
						position: relative;
						width: 100%;
						height: 100%;

						.click-area {
							width: 100%;
							height: 100%;
							position: absolute;
							z-index: 1;
						}

						&:hover {
							.ui-button {
								transition: all linear 0.35s;
								opacity: 0.9;
							}
						}
					}
				}
			}

			.content {
				display: flex;
				flex-direction: column;
				width: 100%;

				.title {
					color: var(--var-black-100);
				}

				.subtitle {
					color: var(--var-gray-200);
				}
			}
		}

		&_filled {
			background: var(--var-white-400);

			display: flex;
			align-items: center;
			padding: $p-0 $p-1;
			border-radius: $br-1;
			gap: $g-3;
			justify-content: space-between;

			.btn {
				&__delete {
					cursor: pointer;
				}
			}

			.content {
				display: flex;
				flex-direction: column;
				width: 100%;

				.title {
					color: var(--var-black-100);
				}

				.subtitle {
					color: var(--var-gray-200);
				}
			}
		}
	}

	.flare {
		position: absolute;
		opacity: 0.65;
		top: 0;
		height: 100%;
		width: 15%;
		transform: skewX(-45deg);
		left: -10%;
		background: linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.4));
		animation: flareAnimation 1s infinite ease-out;
		z-index: 9999;
	}

	@keyframes flareAnimation {
		0% {
			left: -10%;
		}
		100% {
			left: 110%;
		}
	}
</style>

<style lang="scss">
	.picker-menu-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.5);
		z-index: 999999;
		display: flex;
		align-items: flex-end;
		justify-content: center;
	}

	.picker-menu-sheet {
		width: 100%;
		max-width: 500px;
		background: #ffffff;
		border-radius: 20px 20px 0 0;
		padding: 16px;
		padding-bottom: max(16px, env(safe-area-inset-bottom));
		display: flex;
		flex-direction: column;
		gap: 8px;

		&__handle {
			width: 40px;
			height: 4px;
			background: #e0e0e0;
			border-radius: 2px;
			margin: 0 auto 12px;
		}

		&__title {
			font-size: 16px;
			font-weight: 600;
			color: #000000;
			text-align: center;
			margin-bottom: 8px;
		}

		&__item {
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 14px 16px;
			border: none;
			background: #f5f5f5;
			border-radius: 12px;
			cursor: pointer;
			font-size: 16px;
			color: #000000;
			text-align: left;
			transition: background 0.2s;

			&:hover {
				background: #ebebeb;
			}

			&:active {
				background: #e0e0e0;
			}
		}

		&__cancel {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 14px 16px;
			border: none;
			background: transparent;
			border-radius: 12px;
			cursor: pointer;
			font-size: 16px;
			color: #ff3b30;
			margin-top: 8px;
			transition: background 0.2s;

			&:hover {
				background: #fff0f0;
			}

			&:active {
				background: #ffe0e0;
			}
		}
	}

	.picker-menu-enter-active,
	.picker-menu-leave-active {
		transition: opacity 0.25s ease;

		.picker-menu-sheet {
			transition: transform 0.25s ease;
		}
	}

	.picker-menu-enter-from,
	.picker-menu-leave-to {
		opacity: 0;

		.picker-menu-sheet {
			transform: translateY(100%);
		}
	}
</style>
